import { useMemo, useState } from "react";
import {
    FiCheckCircle,
    FiCreditCard,
    FiFolder,
    FiPlusCircle,
    FiTrash2,
} from "react-icons/fi";
import AppLayout from "../components/common/AppLayout";
import ConfirmModal from "../components/common/ConfirmModal";
import DeudaForm from "../components/deudas/DeudaForm";
import CuotaForm from "../components/deudas/CuotaForm";
import PagoCuotaForm from "../components/deudas/PagoCuotaForm";
import GenerarCuotasForm from "../components/deudas/GenerarCuotasForm";
import EditarCuotaForm from "../components/deudas/EditarCuotaForm";
import CuotasTable from "../components/deudas/CuotasTable";
import useDeudas from "../hooks/useDeudas";

export default function DeudasPage() {
    const {
        deudas,
        cuotas,
        loading,
        loadingCuotas,
        guardar,
        verCuotas,
        agregarCuota,
        generarCuotas,
        editarCuota,
        eliminarCuota,
        pagarCuota,
        eliminar,
    } = useDeudas();

    const [openForm, setOpenForm] = useState(false);
    const [openCuotaForm, setOpenCuotaForm] = useState(false);
    const [openPagoForm, setOpenPagoForm] = useState(false);
    const [openGenerarCuotas, setOpenGenerarCuotas] = useState(false);
    const [openEditarCuota, setOpenEditarCuota] = useState(false);

    const [confirmEliminarOpen, setConfirmEliminarOpen] = useState(false);
    const [confirmEliminarCuotaOpen, setConfirmEliminarCuotaOpen] = useState(false);

    const [editing, setEditing] = useState(null);
    const [selectedDeuda, setSelectedDeuda] = useState(null);
    const [selectedCuota, setSelectedCuota] = useState(null);

    const [tabEstado, setTabEstado] = useState("ACTIVAS");

    const deudasFiltradas = useMemo(() => {
        if (tabEstado === "TODAS") return deudas;

        if (tabEstado === "ACTIVAS") {
            return deudas.filter((d) => d.estado === "ACTIVA");
        }

        if (tabEstado === "FINALIZADAS") {
            return deudas.filter(
                (d) => d.estado === "FINALIZADA" || Number(d.saldoPendiente || 0) <= 0
            );
        }

        if (tabEstado === "ELIMINADAS") {
            return deudas.filter((d) => d.estado === "ELIMINADA");
        }

        return deudas;
    }, [deudas, tabEstado]);

    return (
        <AppLayout
            title={
                <span style={{ display: "inline-flex", alignItems: "center", gap: 10 }}>
                    <FiCreditCard size={30} />
                    Deudas y créditos
                </span>
            }
        >
            <div className="toolbar-row">
                <button
                    className="btn-primary"
                    type="button"
                    onClick={() => {
                        setEditing(null);
                        setOpenForm(true);
                    }}
                >
                    <FiPlusCircle size={18} />
                    Nueva deuda
                </button>
            </div>

            <div
                className="card-panel"
                style={{ marginBottom: 18, display: "flex", gap: 10, flexWrap: "wrap" }}
            >
                <button
                    type="button"
                    className={tabEstado === "ACTIVAS" ? "btn-primary" : "btn-secondary"}
                    onClick={() => setTabEstado("ACTIVAS")}
                >
                    <FiCheckCircle size={16} />
                    Activas
                </button>

                <button
                    type="button"
                    className={tabEstado === "FINALIZADAS" ? "btn-primary" : "btn-secondary"}
                    onClick={() => setTabEstado("FINALIZADAS")}
                >
                    <FiFolder size={16} />
                    Finalizadas
                </button>

                <button
                    type="button"
                    className={tabEstado === "ELIMINADAS" ? "btn-primary" : "btn-secondary"}
                    onClick={() => setTabEstado("ELIMINADAS")}
                >
                    <FiTrash2 size={16} />
                    Eliminadas
                </button>

                <button
                    type="button"
                    className={tabEstado === "TODAS" ? "btn-primary" : "btn-secondary"}
                    onClick={() => setTabEstado("TODAS")}
                >
                    Todas
                </button>
            </div>

            {loading ? (
                <p>Cargando...</p>
            ) : (
                <div className="table-wrap">
                    <table className="table-app">
                        <thead>
                        <tr>
                            <th>Tipo</th>
                            <th>Entidad</th>
                            <th>Monto inicial</th>
                            <th>Saldo pendiente</th>
                            <th>Estado</th>
                            <th>Acción</th>
                        </tr>
                        </thead>
                        <tbody>
                        {deudasFiltradas.length === 0 ? (
                            <tr>
                                <td colSpan="6" className="empty-state">
                                    No hay deudas en esta pestaña
                                </td>
                            </tr>
                        ) : (
                            deudasFiltradas.map((row) => (
                                <tr key={row.idDeuda}>
                                    <td>{row.tipo}</td>
                                    <td>{row.entidad}</td>
                                    <td>S/ {Number(row.montoInicial || 0).toFixed(2)}</td>
                                    <td>S/ {Number(row.saldoPendiente || 0).toFixed(2)}</td>
                                    <td>{row.estado}</td>
                                    <td>
                                        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                                            <button
                                                type="button"
                                                className="btn-secondary"
                                                onClick={() => {
                                                    setEditing(row);
                                                    setOpenForm(true);
                                                }}
                                            >
                                                Editar
                                            </button>

                                            {row.estado !== "ELIMINADA" && (
                                                <>
                                                    <button
                                                        type="button"
                                                        className="btn-secondary"
                                                        onClick={async () => {
                                                            setSelectedDeuda(row);
                                                            await verCuotas(row.idDeuda);
                                                        }}
                                                    >
                                                        Ver cuotas
                                                    </button>

                                                    <button
                                                        type="button"
                                                        className="btn-danger"
                                                        onClick={() => {
                                                            setSelectedDeuda(row);
                                                            setConfirmEliminarOpen(true);
                                                        }}
                                                    >
                                                        Eliminar
                                                    </button>
                                                </>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            ))
                        )}
                        </tbody>
                    </table>
                </div>
            )}

            {selectedDeuda && selectedDeuda.estado !== "ELIMINADA" && (
                <div className="card-panel" style={{ marginTop: 20 }}>
                    <h3>Cuotas de {selectedDeuda.entidad}</h3>

                    <CuotasTable
                        data={cuotas}
                        loading={loadingCuotas}
                        onAgregarCuota={() => setOpenCuotaForm(true)}
                        onGenerarCuotas={() => setOpenGenerarCuotas(true)}
                        onEditarCuota={(cuota) => {
                            setSelectedCuota(cuota);
                            setOpenEditarCuota(true);
                        }}
                        onEliminarCuota={(cuota) => {
                            setSelectedCuota(cuota);
                            setConfirmEliminarCuotaOpen(true);
                        }}
                        onPagar={(cuota) => {
                            setSelectedCuota(cuota);
                            setOpenPagoForm(true);
                        }}
                    />
                </div>
            )}

            {openForm && (
                <DeudaForm
                    initialData={editing}
                    onClose={() => setOpenForm(false)}
                    onSave={async (payload) => {
                        await guardar(payload, editing?.idDeuda);
                        setOpenForm(false);
                    }}
                />
            )}

            {openCuotaForm && selectedDeuda && (
                <CuotaForm
                    deuda={selectedDeuda}
                    onClose={() => setOpenCuotaForm(false)}
                    onSave={async (payload) => {
                        await agregarCuota(selectedDeuda.idDeuda, payload);
                        setOpenCuotaForm(false);
                    }}
                />
            )}

            {openGenerarCuotas && selectedDeuda && (
                <GenerarCuotasForm
                    deuda={selectedDeuda}
                    onClose={() => setOpenGenerarCuotas(false)}
                    onSave={async (payload) => {
                        await generarCuotas(
                            selectedDeuda.idDeuda,
                            payload.cantidadCuotas,
                            payload.primerVencimiento,
                            payload.montoPorCuota
                        );
                        setOpenGenerarCuotas(false);
                    }}
                />
            )}

            {openEditarCuota && selectedCuota && selectedDeuda && (
                <EditarCuotaForm
                    cuota={selectedCuota}
                    onClose={() => setOpenEditarCuota(false)}
                    onSave={async (payload) => {
                        await editarCuota(
                            selectedCuota.idCuota,
                            payload,
                            selectedDeuda.idDeuda
                        );
                        setOpenEditarCuota(false);
                    }}
                />
            )}

            {openPagoForm && selectedCuota && selectedDeuda && (
                <PagoCuotaForm
                    cuota={selectedCuota}
                    onClose={() => setOpenPagoForm(false)}
                    onSave={async (payload) => {
                        await pagarCuota(
                            selectedCuota.idCuota,
                            payload,
                            selectedDeuda.idDeuda
                        );
                        setOpenPagoForm(false);
                    }}
                />
            )}

            <ConfirmModal
                open={confirmEliminarOpen}
                title="Eliminar deuda"
                message={`¿Seguro que deseas eliminar la deuda de "${selectedDeuda?.entidad || ""}"? Esta acción la moverá a la pestaña Eliminadas.`}
                confirmText="Sí, eliminar"
                cancelText="Cancelar"
                variant="danger"
                onClose={() => setConfirmEliminarOpen(false)}
                onConfirm={async () => {
                    await eliminar(selectedDeuda.idDeuda);
                    setConfirmEliminarOpen(false);
                }}
            />

            <ConfirmModal
                open={confirmEliminarCuotaOpen}
                title="Eliminar cuota"
                message={`¿Seguro que deseas eliminar la cuota #${selectedCuota?.numeroCuota || ""}?`}
                confirmText="Sí, eliminar"
                cancelText="Cancelar"
                variant="danger"
                onClose={() => setConfirmEliminarCuotaOpen(false)}
                onConfirm={async () => {
                    await eliminarCuota(selectedCuota.idCuota, selectedDeuda.idDeuda);
                    setConfirmEliminarCuotaOpen(false);
                }}
            />
        </AppLayout>
    );
}